import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Radio, Checkbox, Space, Typography, Spin, message, Alert } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { generateUserAgentHash } from '../../core/utils/helpers';

import logo from '../../assets/images/logo-full-transparent.webp';

import { AppDispatch, CombinedStates } from '../../core/types';
import Main from '../../components/layout/Main';
import { getSurveyBySlug, submitSurveyResponse } from '../../core/survey/surveyApi';
import { Question } from '../../core/survey/types';
import { SurveyFormValues } from './types';

import './surveyView.scss';

const { Title, Paragraph } = Typography;

const SurveyView = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const { slug } = useParams<{ slug: string }>();

    const {
        singleSurvey,
        isFetching,
        hasSubmitted,
    } = useSelector((state:CombinedStates) => state.survey);

    const [questions, setQuestions] = useState<Question[]>(singleSurvey?.questions || []);

    // Check if survey has parent and should show parent questions
    const hasParent = singleSurvey?.parent_id !== null && singleSurvey?.parent_id !== undefined;
    const shouldShowParentQuestions = hasParent && singleSurvey?.parent?.questions;

    useEffect(() => {
        console.log('slug', slug);

        // Paruošiame user agent informaciją prieš gaunant survey
        const userAgentHash = generateUserAgentHash();
        const { userAgent } = navigator;

        // Siunčiame survey su user agent informacija
        dispatch(getSurveyBySlug({
            slug,
            userAgent,
            userAgentHash,
        }));
    }, [slug]);

    useEffect(() => {
        if (shouldShowParentQuestions) {
            // Show parent questions when parent exists
            setQuestions(singleSurvey?.parent?.questions || []);
        } else if (singleSurvey?.questions) {
            // Show own questions when no parent
            setQuestions(singleSurvey.questions);
        }
    }, [singleSurvey?.questions, singleSurvey?.parent?.questions, shouldShowParentQuestions]);

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];

    const handleFinish = async (values: SurveyFormValues) => {
        // Patikriname, ar vartotojas jau pateikė atsakymus
        if (hasSubmitted) {
            message.warning('Jūs jau pateikėte atsakymus šiai apklausai!');
            return;
        }

        // Patikriname, ar apklausa aktyvi
        if (!singleSurvey?.is_active) {
            message.error('Apklausa šiuo metu neaktyvi!');
            return;
        }

        // Patikriname, ar apklausa turi teisingą statusą
        if (singleSurvey?.status !== 1) {
            message.error('Apklausa šiuo metu negalima pildyti!');
            return;
        }

        // Paruošiame duomenis su user agent informacija backend'ui
        const userAgentHash = generateUserAgentHash();
        const { userAgent } = navigator;

        const submissionData = {
            surveyId: singleSurvey?.id || 0,
            surveySlug: slug || '',
            userAgent,
            userAgentHash,
            answers: values,
            submittedAt: new Date().toISOString(),
        };

        dispatch(submitSurveyResponse(submissionData));
    };

    const handleFinishFailed = (errorInfo: { errorFields?: Array<{ name: string | number | (string | number)[] }> }) => {
        // Gauname pirmą klaidą iš errorInfo
        const firstError = errorInfo.errorFields?.[0];
        if (firstError) {
            // Scrolliname į pirmą klaidingą lauką
            form.scrollToField(firstError.name);

            // Parodome pranešimą vartotojui
            message.error('Prašome užpildyti visus privalomus laukus');
        }
    };

    // Funkcija apklausos aktyvumo laikotarpio formatavimui
    const formatActivePeriod = () => {
        if (!singleSurvey) return null;

        const { active_from, active_until } = singleSurvey;

        if (!active_from && !active_until) {
            return null;
        }

        let periodText = '';

        if (active_from && active_until) {
            periodText = `Apklausa aktyvi nuo ${dayjs(active_from).format('YYYY-MM-DD HH:mm')} iki ${dayjs(active_until).format('YYYY-MM-DD HH:mm')}`;
        } else if (active_from) {
            periodText = `Apklausa aktyvi nuo ${dayjs(active_from).format('YYYY-MM-DD HH:mm')}`;
        } else if (active_until) {
            periodText = `Apklausa aktyvi iki ${dayjs(active_until).format('YYYY-MM-DD HH:mm')}`;
        }

        return periodText;
    };

    // Funkcija apklausos statuso tikrinimui
    const getSurveyStatusMessage = () => {
        if (!singleSurvey) return null;

        switch (singleSurvey.status) {
        case 0:
            return {
                title: '🔨 Apklausa kuriama',
                message: 'Ši apklausa vis dar kuriama. Bandykite vėliau.',
                type: 'info' as const,
                showForm: false,
            };
        case 1:
            return {
                title: null,
                message: null,
                type: null,
                showForm: true,
            };
        case 3:
            return {
                title: '⏸️ Apklausa sustabdyta',
                message: 'Ši apklausa buvo sustabdyta administratoriaus.',
                type: 'warning' as const,
                showForm: false,
            };
        default:
            return {
                title: 'Nežinomas statusas',
                message: 'Apklausos statusas nežinomas.',
                type: 'error' as const,
                showForm: false,
            };
        }
    };

    const surveyStatus = getSurveyStatusMessage();

    return (
        <Main
            className="survey-view-page"
            title={<div>VKSIS Apklausa</div>}
            headerTitle={<div><img src={logo} alt="logo" style={{ height: '80px' }} /></div>}
            menuWidth={350}
            menuPlacement="left"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent={<div />}
            isPublic
            floatingFooter
            backLink={false}
            breadcrumb={null}>
            <Spin spinning={isFetching}>
                <Card style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
                    <Title level={4} style={{ marginBottom: '16px', textAlign: 'center' }}>{singleSurvey?.title}</Title>
                    {singleSurvey?.start_text && <Paragraph className="start-text">{singleSurvey?.start_text}</Paragraph>}

                    {/* Aktyvumo laikotarpio informacija */}
                    {formatActivePeriod() && (
                        <Alert
                            message={(
                                <Space>
                                    <ClockCircleOutlined />
                                    {formatActivePeriod()}
                                </Space>
                            )}
                            type="info"
                            style={{ marginBottom: '24px' }}
                            showIcon />
                    )}

                    {(() => {
                        if (hasSubmitted) {
                            return (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <Title level={3} style={{ color: '#52c41a' }}>✓ Atsakymai pateikti</Title>
                                    <Paragraph>
                                        Jūs jau pateikėte atsakymus šiai apklausai. Ačiū už dalyvavimą!
                                    </Paragraph>
                                </div>
                            );
                        }

                        // Patikriname, ar apklausa turi būti rodoma pagal statusą
                        if (surveyStatus && !surveyStatus.showForm) {
                            return (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <Title level={3} style={{ color: surveyStatus.type === 'info' ? '#1890ff' : '#faad14' }}>
                                        {surveyStatus.title}
                                    </Title>
                                    <Paragraph>
                                        {surveyStatus.message}
                                    </Paragraph>
                                </div>
                            );
                        }

                        // Patikriname, ar apklausa aktyvi (papildomas patikrinimas)
                        if (!singleSurvey?.is_active) {
                            return (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <Title level={3} style={{ color: '#faad14' }}>⏰ Apklausa neaktyvi</Title>
                                    <Paragraph>
                                        Šios apklausos šiuo metu negalima pildyti. Bandykite vėliau arba kreipkitės į administratorių.
                                    </Paragraph>
                                </div>
                            );
                        }

                        return (
                            <Form layout="vertical" form={form} onFinish={handleFinish} onFinishFailed={handleFinishFailed}>
                                {questions?.map((q: Question) => {
                                    if (q.type === 'paragraph') {
                                        return (
                                            <div
                                                key={q.id}
                                                style={{
                                                    margin: '32px 0',
                                                    padding: '16px',
                                                    background: '#fafafa',
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: '8px',
                                                }}>
                                                <Title level={5} style={{ marginBottom: 8 }}>{q.title}</Title>
                                                {q.description && <Paragraph style={{ margin: 0 }}>{q.description}</Paragraph>}
                                            </div>
                                        );
                                    }

                                    if (q.type === 'text') {
                                        return (
                                            <Form.Item
                                                key={q.id}
                                                name={q.id}
                                                label={q.title}
                                                rules={q.is_required === 1 ? [{ required: true, message: 'Šis klausimas privalomas' }] : []}>
                                                <Input size="large" placeholder="Jūsų atsakymas" />
                                            </Form.Item>
                                        );
                                    }

                                    if (q.type === 'single') {
                                        return (
                                            <Form.Item
                                                key={q.id}
                                                name={q.id}
                                                label={q.title}
                                                rules={q.is_required === 1 ? [{ required: true, message: 'Pasirinkite vieną variantą' }] : []}>
                                                <Radio.Group>
                                                    <Space direction="vertical">
                                                        {q.options?.map(opt => (
                                                            <Radio key={opt.id} value={opt.id}>{opt.title}</Radio>
                                                        ))}
                                                    </Space>
                                                </Radio.Group>
                                            </Form.Item>
                                        );
                                    }

                                    if (q.type === 'multiple') {
                                        return (
                                            <Form.Item
                                                key={q.id}
                                                name={q.id}
                                                label={q.title}
                                                rules={q.is_required === 1 ? [{ required: true, message: 'Pasirinkite bent vieną variantą' }] : []}>
                                                <Checkbox.Group>
                                                    <Space direction="vertical">
                                                        {q.options?.map(opt => (
                                                            <Checkbox key={opt.id} value={opt.id}>{opt.title}</Checkbox>
                                                        ))}
                                                    </Space>
                                                </Checkbox.Group>
                                            </Form.Item>
                                        );
                                    }

                                    return null;
                                })}

                                {singleSurvey?.end_text && <Paragraph className="end-text">{singleSurvey?.end_text}</Paragraph>}
                                {singleSurvey?.is_premade === 1 ? (
                                    <Alert type="info" showIcon message="Norint pateikti atsakymus prašome sukurti naują apklausą šios apklausos pagrindu." />
                                ) : (
                                    <Form.Item>
                                        <Button loading={isFetching} size="large" type="primary" htmlType="submit" block>
                                            Pateikti atsakymus
                                        </Button>
                                    </Form.Item>
                                )}

                            </Form>
                        );
                    })()}
                </Card>
            </Spin>

        </Main>
    );
};

export default SurveyView;

SurveyView.propTypes = {};
